import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { auditLogs } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Scroll, MagnifyingGlass, Clock, MapPin, UserCircle } from "@phosphor-icons/react";

const ACTION_LABEL = {
  "user.login": "User logged in", "user.invited": "User invited", "user.invite_resent": "Invitation resent",
  "user.permission_changed": "Permission / role changed", "user.removed": "User removed",
  "campaign.published": "Campaign published", "ai.content_generated": "AI content generated",
  "shopify.update_approved": "Shopify update applied",
};
const ACTION_COLOR = (a) =>
  a.startsWith("user.removed") || a.includes("disabled") ? "text-red-400"
  : a.startsWith("campaign") || a.startsWith("shopify") ? "text-emerald-400"
  : a.startsWith("ai.") ? "brand-text" : "text-sky-400";

export default function AuditLogsPage() {
  const { clients } = useClient();
  const [search, setSearch] = useState("");
  const logsQ = useQuery({ queryKey: ["audit-logs"], queryFn: () => auditLogs({ limit: 300 }), refetchInterval: 30000 });

  const wsName = useMemo(() => {
    const m = {};
    (clients || []).forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [clients]);

  const rows = (logsQ.data || []).filter((r) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    const detail = `${r.meta?.target_email || ""} ${r.meta?.invited_email || ""} ${r.meta?.title || ""}`.toLowerCase();
    return (r.user_email || "").toLowerCase().includes(s) || (r.action || "").toLowerCase().includes(s)
      || (ACTION_LABEL[r.action] || "").toLowerCase().includes(s) || (wsName[r.workspace_id] || "").toLowerCase().includes(s)
      || detail.includes(s);
  });

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <PageContainer>
      <PageHeader eyebrow="Security" title="Audit Logs"
        subtitle="Append-only record of every important action — logins, invites, permission changes, AI generation, publishing and Shopify writes. Workspace-scoped and tamper-evident.">
        <div className="relative"><MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7a7a7a]" />
          <Input data-testid="audit-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search user / action / workspace…" className="bg-[#10101A] border-white/10 pl-8 h-9 w-72" /></div>
      </PageHeader>

      <div className="card-matte overflow-hidden" data-testid="audit-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 label-caps border-b border-white/10 bg-white/[0.02]">
          <div className="col-span-3">Action</div><div className="col-span-3">User</div>
          <div className="col-span-2">Workspace</div><div className="col-span-2">When</div><div className="col-span-2">Details</div>
        </div>
        <div className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
          {rows.map((r) => (
            <div key={r.id} data-testid={`audit-row-${r.id}`} className="grid grid-cols-12 gap-2 px-4 py-2.5 text-xs items-center">
              <div className={`col-span-3 font-semibold flex items-center gap-1.5 ${ACTION_COLOR(r.action)}`}><Scroll size={12} /> {ACTION_LABEL[r.action] || r.action}</div>
              <div className="col-span-3 text-[#cfcfcf] truncate flex items-center gap-1.5"><UserCircle size={13} className="text-[#7a7a7a]" /> {r.user_name || r.user_email || "—"}</div>
              <div className="col-span-2 text-[#9a9a9a] truncate">{wsName[r.workspace_id] || (r.workspace_id ? "—" : "Agency")}</div>
              <div className="col-span-2 text-[#9a9a9a] flex items-center gap-1"><Clock size={11} /> {fmt(r.at)}</div>
              <div className="col-span-2 text-[#7a7a7a] truncate">
                {r.meta?.target_email || r.meta?.invited_email || r.meta?.title || (r.meta?.changed || []).join(", ") || ""}
                {r.ip ? <span className="flex items-center gap-0.5 text-[10px] mt-0.5"><MapPin size={9} /> {r.ip}</span> : null}
              </div>
            </div>
          ))}
          {!rows.length && <div className="px-4 py-10 text-center text-sm text-[#7a7a7a]">{logsQ.isLoading ? "Loading…" : "No audit events yet. Actions like logins, invites and publishing will appear here."}</div>}
        </div>
      </div>
    </PageContainer>
  );
}
